﻿import * as route from "@arcgis/core/rest/route";
import {
    copyValuesIfExists, 
    hasValue,
    getProtobufGraphicStream
} from "./arcGisJsInterop";
import {
    buildJsPoint, 
    buildJsPolygon, 
    buildJsSpatialReference,
    buildJsFeatureSet
} from "./jsBuilder";
import PolylineBarrier from "@arcgis/core/rest/support/PolylineBarrier";
import PolygonBarrier from "@arcgis/core/rest/support/PolygonBarrier";
import PointBarrier from "@arcgis/core/rest/support/PointBarrier";
import Stop from "@arcgis/core/rest/support/Stop";
import RouteParameters from "@arcgis/core/rest/support/RouteParameters";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import RouteSolveResult from "@arcgis/core/rest/support/RouteSolveResult";
import {
    buildDotNetGraphic, 
    buildDotNetPoint,
    buildDotNetFeatureSet,
    buildDotNetDirectionsFeatureSet
} from "./dotNetBuilder";

export default class RouteWrapper {
    private dotNetRef: any;
    
    constructor(dotNetRef) {
        this.dotNetRef = dotNetRef;
    }

    async solve(url: string, params: any, requestOptions: any) {
        try {
            let jsParams = new RouteParameters();
            if (hasValue(params.outSpatialReference)) {
                jsParams.outSpatialReference = buildJsSpatialReference(params.outSpatialReference);
            }
            if (hasValue(params.pointBarriers)) {
                jsParams.pointBarriers = params.pointBarriers.map((pointBarrier) => {
                    let jsPointBarrier = new PointBarrier();
                    
                    if (hasValue(pointBarrier.geometry)) {
                        jsPointBarrier.geometry = buildJsPoint(pointBarrier.geometry);
                    }
                    
                    copyValuesIfExists(pointBarrier, jsPointBarrier, 'barrierType', 'curbApproach', 'fullEdge',
                        'name');
                    
                    return jsPointBarrier;
                });
            }
            if (hasValue(params.polygonBarriers)) {
                jsParams.polygonBarriers = params.polygonBarriers.map((polygonBarrier) => {
                    let jsPolygonBarrier = new PolygonBarrier();
                    
                    if (hasValue(polygonBarrier.geometry)) {
                        jsPolygonBarrier.geometry = buildJsPolygon(polygonBarrier.geometry);
                    }
                    
                    copyValuesIfExists(polygonBarrier, jsPolygonBarrier, 'barrierType', 'name');
                    
                    return jsPolygonBarrier;
                });
            }
            if (hasValue(params.polylineBarriers)) {
                jsParams.polylineBarriers = params.polylineBarriers.map((polylineBarrier) => {
                    let jsPolylineBarrier = new PolylineBarrier();
                    
                    if (hasValue(polylineBarrier.geometry)) {
                        jsPolylineBarrier.geometry = buildJsPoint(polylineBarrier.geometry);
                    }
                    
                    copyValuesIfExists(polylineBarrier, jsPolylineBarrier, 'barrierType', 'name');
                    
                    return jsPolylineBarrier;
                });
            }
            if (hasValue(params.featureSetStops)) {
                jsParams.stops = buildJsFeatureSet(params.featureSetStops, null);
            }
            if (hasValue(params.travelMode)) {
                jsParams.travelMode = params.travelMode;
            }
            copyValuesIfExists(params, jsParams, 'accumulateAttributes', 'apiKey', 'attributeParameterValues',
                'directionsLanguage', 'directionsLengthUnits', 'directionsOutputType', 'directionsStyleName',
                'directionsTimeAttribute', 'findBestSequence', 'geometryPrecision', 'geometryPrecisionM',
                'geometryPrecisionZ', 'ignoreInvalidLocations', 'impedanceAttribute', 'outputGeometryPrecision',
                'outputGeometryPrecisionUnits', 'outputLines', 'preserveFirstStop', 'preserveLastStop',
                'preserveObjectID', 'restrictionAttributes', 'restrictUTurns', 'returnBarriers', 'returnDirections',
                'returnPolygonBarriers', 'returnPolylineBarriers', 'returnRoutes', 'returnStops', 
                'returnTraversedEdges', 'returnTraversedJunctions', 'returnTraversedTurns', 'returnZ',
                'timeWindowsAreUTC', 'useHierarchy', 'useTimeWindows');
            let result: RouteSolveResult;
            if (requestOptions !== null) {
                result = await route.solve(url, jsParams, requestOptions);
            } else {
                result = await route.solve(url, jsParams);
            }
            let routeResults: any[] = [];
            for (let i = 0; i < result.routeResults.length; i++) {
                let routeResult = result.routeResults[i];
                let directionLines = null;
                if (hasValue(routeResult.directionLines)) {
                    directionLines = await buildDotNetFeatureSet(routeResult.directionLines, null);
                    let directionLinesGraphics = getProtobufGraphicStream(directionLines.features);
                    await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', directionLinesGraphics, 'directionLines', i);
                    directionLines.features = [];
                }
                let directionPoints = null;
                if (hasValue(routeResult.directionPoints)) {
                    directionPoints = await buildDotNetFeatureSet(routeResult.directionPoints, null);
                    let directionPointsGraphics = getProtobufGraphicStream(directionPoints.features);
                    await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', directionPointsGraphics, 'directionPoints', i);
                    directionPoints.features = [];
                }
                let directions = null;
                if (hasValue(routeResult.directions)) {
                    directions = await buildDotNetDirectionsFeatureSet(routeResult.directions, null);
                    let directionsGraphics = getProtobufGraphicStream(directions.features);
                    await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', directionsGraphics, 'directions', i);
                    directions.features = [];
                    let mergedGeometries = getProtobufGraphicStream([{geometry: directions.mergedGeometry} as any]);
                    await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', mergedGeometries, 'mergedGeometry', i);
                    directions.mergedGeometry = null;
                }
                let traversedEdges = null;
                if (hasValue(routeResult.traversedEdges)) {
                    traversedEdges = await buildDotNetFeatureSet(routeResult.traversedEdges, null);
                    let traversedEdgesGraphics = getProtobufGraphicStream(traversedEdges.features);
                    await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', traversedEdgesGraphics, 'traversedEdges', i);
                    traversedEdges.features = [];
                }
                let traversedJunctions = null;
                if (hasValue(routeResult.traversedJunctions)) {
                    traversedJunctions = await buildDotNetFeatureSet(routeResult.traversedJunctions, null);
                    let traversedJunctionsGraphics = getProtobufGraphicStream(traversedJunctions.features);
                    await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', traversedJunctionsGraphics, 'traversedJunctions', i);
                    traversedJunctions.features = [];
                }
                let traversedTurns = null;
                if (hasValue(routeResult.traversedTurns)) {
                    traversedTurns = await buildDotNetFeatureSet(routeResult.traversedTurns, null);
                    let traversedTurnsGraphics = getProtobufGraphicStream(traversedTurns.features);
                    await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', traversedTurnsGraphics, 'traversedTurns', i);
                    traversedTurns.features = [];
                }
                let route = null;
                if (hasValue(routeResult.route)) {
                    route = buildDotNetGraphic(routeResult.route);
                    let routeGraphics = getProtobufGraphicStream([route]);
                    await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', routeGraphics, 'route', i);
                }
                
                if (hasValue(routeResult.stops)) {
                    let stopsGraphics = getProtobufGraphicStream(routeResult.stops.map(buildDotNetGraphic));
                    await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', stopsGraphics, 'stops', i);
                }
                
                routeResults.push({
                    directionLines: directionLines,
                    directionPoints: directionPoints,
                    directions: directions,
                    routeName: routeResult.routeName,
                    traversedEdges: traversedEdges,
                    traversedJunctions: traversedJunctions,
                    traversedTurns: traversedTurns,
                    index: i
                });
            }
            
            if (result.pointBarriers.length > 0) {
                let pointBarriersGraphics = getProtobufGraphicStream(result.pointBarriers.map(buildDotNetGraphic));
                await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', pointBarriersGraphics, 'pointBarriers', null);
            }
            if (result.polygonBarriers.length > 0) {
                let polygonBarriersGraphics = getProtobufGraphicStream(result.polygonBarriers.map(buildDotNetGraphic));
                await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', polygonBarriersGraphics, 'polygonBarriers', null);
            }
            if (result.polylineBarriers.length > 0) {
                let polylineBarriersGraphics = getProtobufGraphicStream(result.polylineBarriers.map(buildDotNetGraphic));
                await this.dotNetRef.invokeMethodAsync('JsGraphicsStreamCallback', polylineBarriersGraphics, 'polylineBarriers', null);
            }
            
            let messages = result.messages.map(message => {
                return {
                    description: message.description,
                    type: message.type
                }
            });
            let dotNetResult = {
                messages: messages,
                routeResults: routeResults
            };
            return dotNetResult;
        } catch (error) {
            console.debug(error);
            throw error;
        }
    }
}